/* Created by Interactive 63 - https://interactive63.com */
/**
 * Colour and stroke edits on a group. A group's own fill is overridden by any fill attribute on
 * the shapes inside it, which is how most design tools export, so editing a group writes to the
 * shapes inside instead. This module decides which elements an edit reaches.
 */
import { canAddProperty, findTrack } from '../timeline/keyframes';
import { CONTAINER_TAGS, type ElementId, type Project, type PropertyKey } from '../model/types';

/** Properties that only show on shapes, and so are written through a group to its shapes. */
export const CASCADING_PROPERTIES: readonly PropertyKey[] = [
  'fill',
  'stroke',
  'fillOpacity',
  'strokeOpacity',
  'strokeWidth',
];

/** Tags of the elements inside a group that actually paint a fill or stroke. */
const PAINTED_TAGS = new Set([
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'text',
  'tspan',
]);

/** Whether an element is a container whose edits to this property should reach its children. */
function cascadesFrom(project: Project, id: ElementId, property: PropertyKey): boolean {
  const tag = project.elements[id]?.tag;
  if (!tag || !CASCADING_PROPERTIES.includes(property) || !CONTAINER_TAGS.includes(tag)) {
    return false;
  }
  // A group animated before this behaviour existed keeps its own track.
  return findTrack(project, id, property) === undefined;
}

/** The shapes inside a container, in document order, that can take the property. */
function paintedDescendants(
  project: Project,
  containerId: ElementId,
  property: PropertyKey,
): ElementId[] {
  const found: ElementId[] = [];
  const visit = (parentId: ElementId): void => {
    for (const childId of project.elements[parentId]?.childIds ?? []) {
      const tag = project.elements[childId]?.tag ?? '';
      if (PAINTED_TAGS.has(tag)) {
        if (canAddProperty(project, childId, property)) found.push(childId);
      } else if (CONTAINER_TAGS.includes(tag)) {
        visit(childId);
      }
    }
  };
  visit(containerId);
  return found;
}

/**
 * The elements an edit to a property should be written to: containers are replaced by the shapes
 * inside them for colour and stroke properties, and everything else passes through. A container
 * with nothing paintable inside is kept, so the edit still lands somewhere.
 */
export function cascadeTargets(
  project: Project,
  ids: readonly ElementId[],
  property: PropertyKey,
): ElementId[] {
  const targets = new Set<ElementId>();
  for (const id of ids) {
    if (!cascadesFrom(project, id, property)) {
      targets.add(id);
      continue;
    }
    const inside = paintedDescendants(project, id, property);
    for (const target of inside.length > 0 ? inside : [id]) targets.add(target);
  }
  return [...targets];
}
